import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

export interface ForecastPoint { time: string; minutes: number; }

export interface ForecastDto {
  horizon_minutes: number;
  points: ForecastPoint[];
  next_threshold_time?: string | null;
  next_threshold_level?: 'LOW' | 'MEDIUM' | 'HIGH' | null;
  next_threshold_minutes?: number | null;
  peak?: ForecastPoint | null;
  delta_vs_baseline?: number | null; // compat
  delta_vs_now?: number | null;      // camelCase côté back -> converti
  last_update?: string | null;
  degraded: boolean;
  wizard_configured?: boolean;
  manual_adjustment?: string | null;

  /** optionnel – baseline pure si renvoyée par /baseline (ou si ajoutée plus tard à /forecast) */
  baseline_points?: ForecastPoint[];
}

@Injectable({ providedIn: 'root' })
export class ForecastService {
  private static readonly MAX_MINUTES_PER_HOUR = 60;
  
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);

  private validateQueueId(queueId: string): void {
    if (!queueId) throw new Error('queueId is required');
  }

  /** Prévision court-terme (horizon en minutes). Tolérant au back camelCase. */
  get(queueId: string, horizonMinutes = 60): Observable<ForecastDto> {
    this.validateQueueId(queueId);
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/forecast`;
    const params = new HttpParams().set('horizon', String(horizonMinutes));
    return this.http.get<unknown>(url, { params }).pipe(map(this.normalize));
  }

  /** Prévision sur la journée entière (24 points horaires) — avec wizard. */
  getDay(queueId: string, anchorIso?: string, dayOfWeek?: number): Observable<ForecastDto> {
    this.validateQueueId(queueId);
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/forecast`;
    let params = new HttpParams().set('fullDay', 'true');
    if (anchorIso) params = params.set('anchor', anchorIso);
    if (dayOfWeek !== undefined) params = params.set('dayOfWeek', String(dayOfWeek));
    return this.http.get<unknown>(url, { params }).pipe(map(this.normalize));
  }

  /** 🔹 Baseline *pure* identique page publique (ouverture + prime, sans wizard). */
  getDayBaseline(queueId: string, anchorIso?: string, dayOfWeek?: number): Observable<ForecastDto> {
    this.validateQueueId(queueId);
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/forecast/baseline`;
    let params = new HttpParams().set('fullDay', 'true');
    if (anchorIso) params = params.set('anchor', anchorIso);
    if (dayOfWeek !== undefined) params = params.set('dayOfWeek', String(dayOfWeek));
    return this.http.get<unknown>(url, { params }).pipe(map(this.normalize));
  }

  /** Normalisation défensive : accepte snake_case ET camelCase. */
  private normalize = (src: any): ForecastDto => {
    const pick = <T>(a: any, snake: string, camel: string, fallback: T): T => {
      const s = a?.[snake];
      const c = a?.[camel];
      return (s !== undefined ? s : (c !== undefined ? c : fallback)) as T;
    };

    const mapPts = (arr: any[]): ForecastPoint[] =>
      Array.isArray(arr)
        ? arr.map((p: any) => ({
          time: String(p?.time ?? ''),
          minutes: Math.max(0, Math.min(ForecastService.MAX_MINUTES_PER_HOUR, Number(p?.minutes ?? 0))),
        }))
        : [];

    const points: ForecastPoint[] = mapPts(pick<any[]>(src, 'points', 'points', []));
    const baseline_points: ForecastPoint[] = mapPts(pick<any[]>(src, 'baseline_points', 'baselinePoints', []));

    const horizon = Number(pick<number>(src, 'horizon_minutes', 'horizonMinutes', 60));

    return {
      horizon_minutes: horizon,
      points,
      baseline_points,
      next_threshold_time: pick<string | null>(src, 'next_threshold_time', 'nextThresholdTime', null),
      next_threshold_level: pick<any>(src, 'next_threshold_level', 'nextThresholdLevel', null),
      next_threshold_minutes: pick<number | null>(src, 'next_threshold_minutes', 'nextThresholdMinutes', null),
      peak: src?.peak
        ? { time: String(src.peak.time ?? src.peak['time']), minutes: Number(src.peak.minutes ?? src.peak['minutes']) }
        : null,
      delta_vs_baseline: pick<number | null>(src, 'delta_vs_baseline', 'deltaVsBaseline', null),
      delta_vs_now: pick<number | null>(src, 'delta_vs_now', 'deltaVsNow', null),
      last_update: pick<string | null>(src, 'last_update', 'lastUpdate', null),
      // 👇 Par défaut, si le back ne précise pas, on considère "dégradé" (pas de wizard)
      degraded: !!pick<boolean>(src, 'degraded', 'degraded', true),
      wizard_configured: pick<boolean | undefined>(src, 'wizard_configured', 'wizardConfigured', undefined),
      manual_adjustment: pick<string | null>(src, 'manual_adjustment', 'manualAdjustment', null),
    };
  };
}